import { useInputControl } from '@conform-to/react'
import type * as SelectPrimitive from '@radix-ui/react-select'
import { REGEXP_ONLY_DIGITS_AND_CHARS, type OTPInputProps } from 'input-otp'
import { MoveLeft } from 'lucide-react'
import React, { useId, useState } from 'react'
import { cn } from '#app/utils/misc.js'
import { Button } from './ui/button.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Combobox } from './ui/combobox.tsx'
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSeparator,
	InputOTPSlot,
} from './ui/input-otp.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectTrigger,
	SelectValue,
} from './ui/select.tsx'
import { Textarea } from './ui/textarea.tsx'

// Define a type for the multi-select props
type MultiSelectProps = Omit<
	SelectPrimitive.SelectProps,
	'value' | 'onValueChange'
> & {
	key?: string
	value?: string[]
	onChange?: (value: string[]) => void
}

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map((e) => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & { key?: string }
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const { key, className: inputClassName, ...props } = inputProps
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				key={key}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...props}
				className={cn('bg-input', inputClassName)}
			/>

			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					{' '}
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}
export function SelectField({
	labelProps,
	inputProps,
	errors,
	className,
	children,
	placeholder,
	triggerClassName,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: SelectPrimitive.SelectProps & { key?: string }
	errors?: ListOfErrors
	className?: string
	children: React.ReactNode
	placeholder?: string
	triggerClassName?: string
}) {
	const errorId = errors?.length ? `error` : undefined
	const { key, ...props } = inputProps
	return (
		<div className={className}>
			<Label {...labelProps} />
			<Select
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				key={key}
				{...props}
			>
				<SelectTrigger className={cn('w-full bg-input px-3', triggerClassName)}>
					<SelectValue placeholder={placeholder ?? 'Select an option'} />
				</SelectTrigger>
				<SelectContent>{children}</SelectContent>
			</Select>

			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function MultiSelectField({
	labelProps,
	inputProps,
	errors,
	className,
	children,
	placeholder,
	triggerClassName,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: MultiSelectProps
	errors?: ListOfErrors
	className?: string
	children: React.ReactNode
	placeholder?: string
	triggerClassName?: string
}) {
	const errorId = errors?.length ? `error` : undefined
	const { key, value = [], onChange, name, ...props } = inputProps
	const fallbackId = useId()
	const id = name ?? fallbackId
	const [isOpen, setIsOpen] = React.useState(false)

	// Create a controlled input for the multi-select
	const input = useInputControl({
		key,
		name: name ?? '',
		formId: props.form ?? '',
		initialValue: value,
	})

	// Handle selection changes
	const handleSelectionChange = (selectedValue: string) => {
		const newValue = value.includes(selectedValue)
			? value.filter((v) => v !== selectedValue)
			: [...value, selectedValue]

		input.change(newValue)
		if (onChange) {
			onChange(newValue)
		}
	}

	// Format the display value
	const displayValue =
		value.length > 0
			? `${value.length} selected`
			: (placeholder ?? 'Select options')

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<div className="relative">
				<Select
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					key={key}
					name={name}
					{...props}
					onValueChange={handleSelectionChange}
					open={isOpen}
					onOpenChange={setIsOpen}
				>
					<SelectTrigger
						className={cn('w-full bg-input px-3', triggerClassName)}
						onClick={() => setIsOpen(!isOpen)}
					>
						<SelectValue>{displayValue}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{React.Children.map(children, (child) => {
							if (React.isValidElement(child)) {
								const childValue = child.props.value as string
								return React.cloneElement(child, {
									...child.props,
									onSelect: (event: Event) => {
										event.preventDefault()
										handleSelectionChange(childValue)
									},
									'data-state': value.includes(childValue)
										? 'checked'
										: 'unchecked',
								})
							}
							return child
						})}
					</SelectContent>
				</Select>

				{value.length > 0 && (
					<div className="mt-2 flex flex-wrap gap-1">
						{value.map((item) => (
							<span
								key={item}
								className="inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
							>
								{item}
								<button
									type="button"
									className="ml-1 rounded-full p-0.5 hover:bg-primary/20"
									onClick={(e) => {
										e.preventDefault()
										handleSelectionChange(item)
									}}
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										width="12"
										height="12"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<line x1="18" y1="6" x2="6" y2="18"></line>
										<line x1="6" y1="6" x2="18" y2="18"></line>
									</svg>
								</button>
							</span>
						))}
					</div>
				)}
			</div>

			{errorId ? (
				<div className="min-h-[32px] px-4 pb-3 pt-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never; key?: string }>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const { key, ...props } = inputProps
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				key={key}
				{...props}
			>
				<InputOTPGroup>
					<InputOTPSlot index={0} />
					<InputOTPSlot index={1} />
					<InputOTPSlot index={2} />
				</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>
					<InputOTPSlot index={3} />
					<InputOTPSlot index={4} />
					<InputOTPSlot index={5} />
				</InputOTPGroup>
			</InputOTP>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
	elementRef,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
		key?: string
	}
	errors?: ListOfErrors
	className?: string
	elementRef?: React.RefObject<HTMLTextAreaElement>
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const { key, className: textareaClassName, ...props } = textareaProps
	return (
		<div className={cn('relative', className)}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				key={key}
				{...props}
				ref={elementRef}
				className={cn('bg-input', textareaClassName)}
			/>
			{errorId ? (
				<div className="mt-1 px-1">
					<ErrorList id={errorId} errors={errors} />
				</div>
			) : null}
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
	const {
		key,
		defaultChecked,
		className: checkboxClassName,
		...checkboxProps
	} = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const { className: labelClassName, ...labelPropsRest } = labelProps

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					className={cn('bg-input', checkboxClassName)}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelPropsRest}
					className={cn(
						'self-center text-body-xs text-muted-foreground',
						labelClassName,
					)}
				/>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function ComboboxField({
	labelProps,
	inputProps,
	errors,
	className,
	options,
	placeholder,
	emptyMessage,
	value,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: SelectPrimitive.SelectProps & { key?: string }
	errors?: ListOfErrors
	className?: string
	options: { value: string; label: React.ReactNode }[]
	placeholder?: string
	emptyMessage?: string
	/**
	 * Current selected value, for when this Combobox is bound to a single
	 * field's value (rather than used as an "add new item into a list"
	 * picker, which is this component's other common usage and doesn't pass
	 * this prop). When provided, the trigger button shows the option's label
	 * instead of the placeholder.
	 */
	value?: string
}) {
	return (
		<div className="flex flex-col gap-2">
			<Label {...labelProps} />
			<Combobox
				options={options}
				value={value}
				placeholder={placeholder}
				emptyMessage={emptyMessage}
				inputProps={{
					...inputProps,
					onChange: (e) => {
						inputProps.onValueChange?.(e.target.value)
					},
					className,
				}}
			/>
			<ErrorList errors={errors} />
		</div>
	)
}

export function ComboboxWithNewItem({
	labelProps,
	inputProps,
	errors,
	className,
	options,
	placeholder,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: SelectPrimitive.SelectProps & { key?: string }
	errors?: ListOfErrors
	className?: string
	options: { value: string; label: string }[]
	placeholder?: string
}) {
	const [opts, setOptions] = useState(options)
	const [selected, setSelected] = useState<string | null>(null)
	const [newInputValue, setNewInputValue] = useState('')

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setNewInputValue(event.target.value)
	}

	const handleAddOption = () => {
		if (newInputValue.trim() !== '') {
			setOptions([...opts, { value: newInputValue, label: newInputValue }])
			setNewInputValue('')
			inputProps.onValueChange?.(newInputValue)
		}
	}

	return (
		<div className="flex items-end gap-2">
			<div className="flex flex-col gap-2">
				<Label {...labelProps} />
				<Combobox
					options={opts}
					value={selected ?? ''}
					setValue={setSelected}
					placeholder={placeholder}
					inputProps={{
						...inputProps,
						className,
						onChange: (e) => {
							inputProps.onValueChange?.(e.target.value)
						},
					}}
				/>
			</div>

			<Button type="button" onClick={handleAddOption} variant="outline">
				<MoveLeft className="h-4 w-4" />
			</Button>
			<div>
				<Field
					labelProps={{ children: 'Add New Option' }}
					inputProps={{
						value: newInputValue,
						onChange: handleInputChange,
						placeholder: 'Enter new option',
					}}
				/>
			</div>
			<ErrorList errors={errors} />
		</div>
	)
}
